/**
 * Voxly — PermissionDeniedDialog.
 *
 * Renders a friendly modal when camera or microphone permission was denied,
 * blocked, missing, or unavailable. Shows step-by-step instructions tailored
 * to the platform (iOS / desktop / iframe / insecure).
 *
 * Usage:
 *   const [denial, setDenial] = useState(null);
 *   const result = await requestMedia({ video: true, audio: false });
 *   if (!result.ok) setDenial(result);
 *   ...
 *   <PermissionDeniedDialog denial={denial} onClose={() => setDenial(null)} onRetry={tryAgain} />
 */
import React from "react";
import { X, ArrowsClockwise, ShieldWarning, Info } from "@phosphor-icons/react";

export default function PermissionDeniedDialog({ denial, onClose, onRetry }) {
  if (!denial) return null;
  const { title, message, howTo, code } = denial;
  const icon = code === "iframe" || code === "insecure" ? Info : ShieldWarning;
  const Icon = icon;
  return (
    <div
      className="fixed inset-0 z-[120] bg-black/75 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="perm-denied-dialog"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-card border border-border rounded-t-3xl sm:rounded-3xl w-full max-w-md flex flex-col"
      >
        <div className="p-5 flex items-start gap-3 border-b border-border">
          <div className="h-12 w-12 shrink-0 rounded-2xl bg-primary/15 text-primary flex items-center justify-center">
            <Icon size={26} weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-heading text-lg font-bold leading-tight" data-testid="perm-denied-title">{title}</h2>
            <p className="text-sm text-muted-foreground mt-0.5">{message}</p>
          </div>
          <button
            onClick={onClose}
            data-testid="perm-denied-close"
            aria-label="Close"
            className="text-muted-foreground hover:text-foreground"
          >
            <X size={20} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">How to fix it</p>
          <ol className="space-y-2.5">
            {(howTo || []).map((step, idx) => (
              <li key={`${idx}:${step}`} className="flex gap-3 text-sm leading-snug" data-testid={`perm-denied-step-${idx}`}>
                <span className="h-6 w-6 shrink-0 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center mt-0.5">
                  {idx + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>
        <div className="p-4 pt-0 flex flex-col-reverse sm:flex-row gap-2">
          <button
            onClick={onClose}
            data-testid="perm-denied-cancel"
            className="flex-1 py-3 rounded-2xl bg-secondary text-foreground font-semibold text-sm hover:bg-secondary/80 transition"
          >
            Not now
          </button>
          {onRetry && (
            <button
              onClick={onRetry}
              data-testid="perm-denied-retry"
              className="flex-1 py-3 rounded-2xl bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition flex items-center justify-center gap-2"
            >
              <ArrowsClockwise size={16} weight="bold" /> Try again
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
